import React, { useEffect, useState } from 'react';
import { usePipeline } from '../../context/PipelineContext';
import { view_node_ip_op, download_node_output, downloadAnalysisWorkbook } from '../../api/api';
import { CiMinimize1, CiMaximize1 } from 'react-icons/ci'; // Import minimize/maximize icons
import { IoCloseSharp } from 'react-icons/io5'; // Import close icon
import { MdOutlineContentCopy } from "react-icons/md"; // Import copy icon
import { TbArrowsMaximize, TbArrowsMinimize } from "react-icons/tb"; // Import expand/collapse icons
import { LuChartLine } from "react-icons/lu"; // Import chart line icon
import { FiDownload } from "react-icons/fi"; // Import download icon
import DataVisualization from './DataVisualization'; // Import the visualization component
import './DataInspector.css';

// Utility function to format numbers with Indian number system and 2 decimal places
const formatIndianNumber = (value) => {
  if (value === null || value === undefined || value === '') return value;
  
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  
  // Round to 2 decimal places
  const rounded = Math.round(num * 100) / 100;
  
  // Convert to string with 2 decimal places
  const numStr = rounded.toFixed(2);
  
  // Split into integer and decimal parts
  const [integerPart, decimalPart] = numStr.split('.');
  
  // Apply Indian number system formatting
  const lastThree = integerPart.slice(-3);
  const otherNumbers = integerPart.slice(0, -3);
  
  if (otherNumbers !== '') {
    const formatted = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
    return formatted + '.' + decimalPart;
  } else {
    return integerPart + '.' + decimalPart;
  }
};

// Node type color mapping
const nodeTypeColors = {
  input: '#fbbf24', // Amber
  normalizer: '#8b5cf6', // Purple
  scaler: '#10b981', // Green
  capper: '#6366f1', // Indigo
  loss: '#ec4899', // Pink
  adder: '#4f46e5', // Indigo (slightly different from capper)
  matcher: '#ea580c', // Orange (for matcher node)
  consumption: '#9333ea', // Purple (for consumption node)
  windgeneration: '#38bdf8', // Sky blue (for wind generation node)
  solargeneration: '#fbbf24', // Amber (for solar generation node)
  banking:'#059669', // Green (for banking node)
  settle: '#7c3aed', // Violet (for settle node)
  extrapolation:'#22c55e',
  finance: '#ec4899', // Red (for finance node)
  loan: '#6366f1', // Indigo (for loan node)
  depreciation: '#dc2626', // Crimson (for depreciation node)
  operationalcosts: '#0ea5e9', // Sky blue (for operational costs node)
  battery: '#0f766e', // Teal (for battery node)
  default: '#6b7280' // Gray (default)
};

// Node type display names
const nodeTypeNames = {
  input: 'Input',
  normalizer: 'Normalizer',
  scaler: 'Scaler',
  capper: 'Capper',
  loss: 'Loss Subtractor',
  adder: 'Adder',
  matcher: 'Matcher',
  consumption: 'Consumption',
  windgeneration: 'Wind Generation',
  solargeneration: 'Solar Generation',
  banking:'banking',
  settle: 'Settle',
  extrapolation:'Extrapolation',
  finance: 'Finance',
  loan: 'Loan',
  depreciation: 'Depreciation',
  operationalcosts: 'Operational Costs',
  battery: 'Battery',
  default: 'Node'
};

const DataInspector = ({ pipelineName, nodeName }) => {
  const { session } = usePipeline();
  const [data, setData] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [copiedColumn, setCopiedColumn] = useState(null);
  const [nodeType, setNodeType] = useState('default');
  const [activeInputTab, setActiveInputTab] = useState(1); // For adder nodes with multiple inputs
  const [activeOutputTab, setActiveOutputTab] = useState(0);
  const [selectedColumns, setSelectedColumns] = useState([]); // For chart visualization
  const [showVisualization, setShowVisualization] = useState(false); // Toggle between table and chart view

  // Find the node type from the session
  useEffect(() => {
    if (session && session.nodes && session.nodes.length > 0) {
      console.log(`All nodes in session:`, session.nodes.map(n => ({ name: n.node_name, type: n.node_type })));
      
      const node = session.nodes.find(n => n.node_name === nodeName);
      if (node && node.node_type) {
        console.log(`Node type for ${nodeName}: ${node.node_type}`);
        setNodeType(node.node_type);
      } else {
        console.log(`No node type found for ${nodeName}, using default`);
        console.log(`Looking for node with name: ${nodeName}`);
        console.log(`Available nodes:`, session.nodes.map(n => n.node_name));
        setNodeType('default');
      }
    }
  }, [session, nodeName]);
  
  // Update node type if we get it from the API
  useEffect(() => {
    if (data && data.node_type) {
      console.log(`Setting node type from API for ${nodeName}: ${data.node_type}`);
      setNodeType(data.node_type);
    }
  }, [data, nodeName]);

  // Reset visibility when component receives new props or session changes
  useEffect(() => {
    setIsVisible(true);
    // Reset expanded state when component is re-rendered
    setIsExpanded(false);
    setIsMinimized(false);
    // Reset active tab to first input
    setActiveInputTab(1);
    setActiveOutputTab(0);
    // Reset visualization state
    setSelectedColumns([]);
    setShowVisualization(false);
    
    // Remove visualization class from container
    setTimeout(() => {
      const container = document.querySelector('.tables-container');
      if (container) {
        container.classList.remove('visualization-active');
      }
    }, 0);
  }, [session.last_run_status, pipelineName, nodeName]);
  
  // Handle column selection for visualization
  const handleColumnSelect = (columnName) => {
    // Skip date, time, and month columns as they are used for x-axis
    if (
      columnName.toLowerCase().includes('date') || 
      columnName.toLowerCase().includes('time') || 
      columnName.toLowerCase().includes('month')
    ) {
      return;
    }
    
    setSelectedColumns(prev => {
      // If column is already selected, remove it
      if (prev.includes(columnName)) {
        return prev.filter(col => col !== columnName);
      }
      // Otherwise add it
      return [...prev, columnName];
    });
  };
  
  // Toggle between table and chart view
  const toggleVisualization = () => {
    // Only allow visualization for matcher and adder nodes
    if (nodeType !== 'matcher' && nodeType !== 'adder') {
      return;
    }
    
    // Toggle visualization state
    setShowVisualization(prev => !prev);
    
    // Add a class to the container for browsers that don't support :has() selector
    setTimeout(() => {
      const container = document.querySelector('.tables-container');
      if (container) {
        if (!showVisualization) {
          container.classList.add('visualization-active');
        } else {
          container.classList.remove('visualization-active');
        }
      }
    }, 0);
  };
  
  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      // Reset states when component unmounts
      setIsVisible(true);
      setIsExpanded(false);
      setIsMinimized(false);
      setSelectedColumns([]);
      setShowVisualization(false);
      
      // Remove visualization class from container
      const container = document.querySelector('.tables-container');
      if (container) {
        container.classList.remove('visualization-active');
      }
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check if the node was included in the last pipeline run
        const isNodeInLastRun = session.last_run_nodes && session.last_run_nodes.includes(nodeName);
        console.log(`Node ${nodeName} in last run: ${isNodeInLastRun}`);
        console.log('Last run nodes:', session.last_run_nodes);
        
        if (!isNodeInLastRun) {
          console.log(`Skipping data fetch for node ${nodeName} as it wasn't in the last run`);
          return;
        }
        
        const result = await view_node_ip_op(pipelineName, nodeName, session.last_run_id);
        console.log('Node data fetched:', result);
        console.log('Has second input:', !!result.node_input_df_2);
        
        // Log the sum data if available
        if (result.sum) {
          console.log('Column sums received:', result.sum);
        } else {
          console.log('No sum data available for this node');
        }
        
        setData(result);
      } catch (error) {
        console.error('Error fetching node data:', error);
      }
    };

    if (session.last_run_status === 'success') {
      fetchData();
    }
  }, [session.last_run_status, session.last_run_nodes, session.last_run_id, pipelineName, nodeName]);
  
  // Function to copy column name to clipboard
  const copyToClipboard = (columnName) => {
    navigator.clipboard.writeText(columnName)
      .then(() => {
        setCopiedColumn(columnName);
        setTimeout(() => setCopiedColumn(null), 2000); // Reset after 2 seconds
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };
  
  // Function to handle closing the inspector
  const handleClose = () => {
    setIsVisible(false);
  };
  
  // Function to toggle minimize state
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
    // If expanding from minimized state, ensure expanded state is off
    if (isMinimized && isExpanded) {
      setIsExpanded(false);
    }
  };
  
  // Function to toggle expand state
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    // If expanding, ensure it's not minimized
    if (!isExpanded && isMinimized) {
      setIsMinimized(false);
    }
  };

  // Get the color for the current node type
  const nodeColor = nodeTypeColors[nodeType] || nodeTypeColors.default;
  const nodeTypeName = nodeTypeNames[nodeType] || nodeTypeNames.default;
  
  // Debug log for node color and type
  useEffect(() => {
    console.log(`Node color for ${nodeName} (${nodeType}): ${nodeColor}`);
    console.log(`Is this a banking node? ${nodeType === 'banking' ? 'YES' : 'NO'}`);
    
    // Log node type details for debugging
    if (data && data.node_type) {
      console.log("Node type from API:", data.node_type);
      console.log("Node type details from API:", data.node_type_details);
      console.log(`Is this a banking node according to API? ${data.node_type === 'banking' ? 'YES' : 'NO'}`);
    }
  }, [nodeName, nodeType, nodeColor, data]);

  // Don't render if data is not available or component is not visible
  if (!isVisible) return null;
  
  // Check if the node was included in the last pipeline run
  const isNodeInLastRun = session.last_run_nodes && session.last_run_nodes.includes(nodeName);
  
  // Show message if pipeline was run but this node wasn't included
  if (session.last_run_status === 'success' && !isNodeInLastRun) {
    return (
      <div className={`data-inspector ${isExpanded ? 'expanded' : ''}`}>
        <div className="data-inspector-header" style={{ backgroundColor: nodeColor }}>
          <h3>
            <span className="inspector-title-text">Data Inspector</span>
            <div className="node-type-badge" style={{ backgroundColor: 'white', color: nodeColor }}>
              {nodeTypeName}
            </div>
            <span className="node-name-text">({nodeName})</span>
          </h3>
          <div className="data-inspector-controls">
            <button onClick={toggleExpand} className="control-button" title={isExpanded ? "Collapse" : "Expand to Header"}>
              {isExpanded ? <TbArrowsMinimize size={20} /> : <TbArrowsMaximize size={20} />}
            </button>
            <button onClick={handleClose} className="control-button" title="Close">
              <IoCloseSharp size={20} />
            </button>
          </div>
        </div>
        <div className="tables-container">
          <div className="no-data-message">
            <p>Run the pipeline once to view the result of this node.</p>
            <p>If node is not configured, please configure it before running the pipeline.</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Show loading state if data is being fetched
  if (!data) {
    return (
      <div className={`data-inspector ${isExpanded ? 'expanded' : ''}`}>
        <div className="data-inspector-header" style={{ backgroundColor: nodeColor }}>
          <h3>
            <span className="inspector-title-text">Data Inspector</span>
            <div className="node-type-badge" style={{ backgroundColor: 'white', color: nodeColor }}>
              {nodeTypeName}
            </div>
            <span className="node-name-text">({nodeName})</span>
          </h3>
          <div className="data-inspector-controls">
            <button onClick={toggleExpand} className="control-button" title={isExpanded ? "Collapse" : "Expand to Header"}>
              {isExpanded ? <TbArrowsMinimize size={20} /> : <TbArrowsMaximize size={20} />}
            </button>
            <button onClick={handleClose} className="control-button" title="Close">
              <IoCloseSharp size={20} />
            </button>
          </div>
        </div>
        <div className="tables-container">
          <p>Loading data for node "{nodeName}"...</p>
        </div>
      </div>
    );
  }

  const additionalOutputs = Array.isArray(data.additional_outputs) ? data.additional_outputs : [];
  const orderedAdditionalOutputs = nodeType === 'banking'
    ? [...additionalOutputs].sort((a, b) => {
        const order = {
          'Unit Allocation Summary': 0,
          'Unit Post-Banking Summary': 1,
          'Portfolio Summary': 2,
          'Unit Pre-Banking Summary': 3,
        };
        const aRank = order[a?.label] ?? 99;
        const bRank = order[b?.label] ?? 99;
        return aRank - bRank;
      })
    : additionalOutputs;

  const outputTables = [
    {
      label: 'Generation',
      rows: data.node_output_df || [],
      length: data.node_output_df_length || data.node_output_df_lenght || 0,
      isPrimary: true,
    },
    ...(orderedAdditionalOutputs.map((output) => ({
      label: output.label || 'Output',
      rows: output.rows || [],
      length: output.length || (output.rows || []).length,
      isPrimary: false,
    }))),
  ];
  const activeOutput = outputTables[activeOutputTab] || outputTables[0];
  const activeOutputRows = activeOutput?.rows || [];
  const activeOutputLength = activeOutput?.length || activeOutputRows.length;

  return (
    <div className={`data-inspector ${isMinimized ? 'minimized' : ''} ${isExpanded ? 'expanded' : ''}`}>
      <div className="data-inspector-header" style={{ backgroundColor: nodeColor }}>
        <h3>
          <span className="inspector-title-text">Data Inspector</span>
          <div className="node-type-badge" style={{ backgroundColor: 'white', color: nodeColor }}>
            {nodeTypeName}
          </div>
          <span className="node-name-text">({nodeName})</span>
        </h3>
        <div className="data-inspector-controls">
          {/* Show visualization toggle for matcher and adder nodes */}
          {(nodeType === 'matcher' || nodeType === 'adder') && (
            <button 
              onClick={toggleVisualization} 
              className={`control-button ${showVisualization ? 'active' : ''}`} 
              title={showVisualization ? "Show Table View" : "Show Chart View"}
            >
              <LuChartLine size={20} />
            </button>
          )}
          <button onClick={toggleExpand} className="control-button" title={isExpanded ? "Collapse" : "Expand to Header"}>
            {isExpanded ? <TbArrowsMinimize size={20} /> : <TbArrowsMaximize size={20} />}
          </button>
          <button onClick={toggleMinimize} className="control-button" title={isMinimized ? "Maximize" : "Minimize"}>
            {isMinimized ? <CiMaximize1 size={20} /> : <CiMinimize1 size={20} />}
          </button>
          <button onClick={handleClose} className="control-button" title="Close">
            <IoCloseSharp size={20} />
          </button>
        </div>
      </div>
      {!isMinimized && (
        <div className="tables-container">
          {/* Hide input data section when visualization is shown for matcher or adder nodes */}
          {!((nodeType === 'matcher' || nodeType === 'adder') && showVisualization) && (
            <>
              <div className="table-container">
                <h4 className="table-title" style={{ borderBottomColor: nodeColor }}>
                  Node Input Data 
                  <span style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#666', marginLeft: '4px' }}>({nodeName})</span>
                </h4>
                
                {/* Show tabs if there are multiple inputs */}
                {((data.node_input_df_2 && Array.isArray(data.node_input_df_2) && data.node_input_df_2.length > 0) || 
                  (data.node_input_df_3 && Array.isArray(data.node_input_df_3) && data.node_input_df_3.length > 0)) && (
                  <div className="input-tabs">
                    <div 
                      className={`input-tab ${activeInputTab === 1 ? 'active' : ''}`} 
                      onClick={() => setActiveInputTab(1)}
                      style={{ '--tab-color': nodeColor }}
                    >
                      {nodeType === 'operationalcosts' ? 'Extrapolation Input' : 'Input 1'}
                      <span className="tab-row-count">{data.node_input_df_1.length}</span>
                    </div>
                    {data.node_input_df_2 && Array.isArray(data.node_input_df_2) && data.node_input_df_2.length > 0 && (
                      <div 
                        className={`input-tab ${activeInputTab === 2 ? 'active' : ''}`} 
                        onClick={() => setActiveInputTab(2)}
                        style={{ '--tab-color': nodeColor }}
                      >
                        {nodeType === 'operationalcosts' ? 'Loan Input' : 'Input 2'}
                        <span className="tab-row-count">{data.node_input_df_2.length}</span>
                      </div>
                    )}
                    {data.node_input_df_3 && Array.isArray(data.node_input_df_3) && data.node_input_df_3.length > 0 && (
                      <div 
                        className={`input-tab ${activeInputTab === 3 ? 'active' : ''}`} 
                        onClick={() => setActiveInputTab(3)}
                        style={{ '--tab-color': nodeColor }}
                      >
                        {nodeType === 'operationalcosts' ? 'Depreciation Input' : 'Input 3'}
                        <span className="tab-row-count">{data.node_input_df_3.length}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Show Input 1 if it's active or if there are no other inputs */}
                {(activeInputTab === 1 || (((!data.node_input_df_2 || !Array.isArray(data.node_input_df_2) || data.node_input_df_2.length === 0)) && ((!data.node_input_df_3 || !Array.isArray(data.node_input_df_3) || data.node_input_df_3.length === 0)))) && (
                  <div className="scrollable-table">
                    <table>
                      <thead>
                        <tr>
                          {(() => {
                            // Get all column keys
                            const keys = Object.keys(data.node_input_df_1[0]);
                            
                            // Check if "year" column exists (case insensitive)
                            const yearColumnIndex = keys.findIndex(key => 
                              key.toLowerCase() === 'year'
                            );
                            
                            // If year column exists, reorder keys to put it first
                            let orderedKeys = [...keys];
                            if (yearColumnIndex !== -1) {
                              const yearColumn = orderedKeys.splice(yearColumnIndex, 1)[0];
                              orderedKeys = [yearColumn, ...orderedKeys];
                            }
                            
                            return orderedKeys.map((key) => (
                              <th key={key} className="column-header">
                                <div className="column-header-content">
                                  <span>{key}</span>
                                  <button 
                                    className="copy-button"
                                    onClick={() => copyToClipboard(key)}
                                    title="Copy column name"
                                  >
                                    <MdOutlineContentCopy 
                                      size={16} 
                                      color={copiedColumn === key ? "#4CAF50" : "#666"} 
                                    />
                                  </button>
                                </div>
                              </th>
                            ));
                          })()}
                        </tr>
                      </thead>
                      <tbody>
                        {data.node_input_df_1.map((row, index) => {
                          // Get all column keys
                          const keys = Object.keys(row);
                          
                          // Check if "year" column exists (case insensitive)
                          const yearColumnIndex = keys.findIndex(key => 
                            key.toLowerCase() === 'year'
                          );
                          
                          // If year column exists, reorder keys to put it first
                          let orderedKeys = [...keys];
                          if (yearColumnIndex !== -1) {
                            const yearColumn = orderedKeys.splice(yearColumnIndex, 1)[0];
                            orderedKeys = [yearColumn, ...orderedKeys];
                          }
                          
                          return (
                            <tr key={index}>
                              {orderedKeys.map((key, idx) => (
                                <td key={idx}>
                                  {((nodeType === 'extrapolation' || nodeType === 'finance') || (nodeType === 'operationalcosts' && activeInputTab === 1)) && typeof row[key] === 'number' ? formatIndianNumber(row[key]) : row[key]}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="row-info">
                      Showing {data.node_input_df_1.length} rows of {data.node_input_df_length1 || data.node_input_df_lenght1}
                    </div>
                  </div>
                )}
                
                {/* Show Input 2 if it's active and exists */}
                {activeInputTab === 2 && data.node_input_df_2 && data.node_input_df_2.length > 0 && (
                  <div className="scrollable-table">
                    <table>
                      <thead>
                        <tr>
                          {(() => {
                            // Get all column keys
                            const keys = Object.keys(data.node_input_df_2[0]);
                            
                            // Check if "year" column exists (case insensitive)
                            const yearColumnIndex = keys.findIndex(key => 
                              key.toLowerCase() === 'year'
                            );
                            
                            // If year column exists, reorder keys to put it first
                            let orderedKeys = [...keys];
                            if (yearColumnIndex !== -1) {
                              const yearColumn = orderedKeys.splice(yearColumnIndex, 1)[0];
                              orderedKeys = [yearColumn, ...orderedKeys];
                            }
                            
                            return orderedKeys.map((key) => (
                              <th key={key} className="column-header">
                                <div className="column-header-content">
                                  <span>{key}</span>
                                  <button 
                                    className="copy-button"
                                    onClick={() => copyToClipboard(key)}
                                    title="Copy column name"
                                  >
                                    <MdOutlineContentCopy 
                                      size={16} 
                                      color={copiedColumn === key ? "#4CAF50" : "#666"} 
                                    />
                                  </button>
                                </div>
                              </th>
                            ));
                          })()}
                        </tr>
                      </thead>
                      <tbody>
                        {data.node_input_df_2.map((row, index) => {
                          // Get all column keys
                          const keys = Object.keys(row);
                          
                          // Check if "year" column exists (case insensitive)
                          const yearColumnIndex = keys.findIndex(key => 
                            key.toLowerCase() === 'year'
                          );
                          
                          // If year column exists, reorder keys to put it first
                          let orderedKeys = [...keys];
                          if (yearColumnIndex !== -1) {
                            const yearColumn = orderedKeys.splice(yearColumnIndex, 1)[0];
                            orderedKeys = [yearColumn, ...orderedKeys];
                          }
                          
                          return (
                            <tr key={index}>
                              {orderedKeys.map((key, idx) => (
                                <td key={idx}>
                                  {((nodeType === 'extrapolation' || nodeType === 'finance') || (nodeType === 'operationalcosts' && activeInputTab === 1)) && typeof row[key] === 'number' ? formatIndianNumber(row[key]) : row[key]}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="row-info">
                      Showing {data.node_input_df_2.length} rows of {data.node_input_df_length2 || data.node_input_df_lenght2 || "unknown"}
                    </div>
                  </div>
                )}
                
                {/* Show Input 3 if it's active and exists */}
                {activeInputTab === 3 && data.node_input_df_3 && data.node_input_df_3.length > 0 && (
                  <div className="scrollable-table">
                    <table>
                      <thead>
                        <tr>
                          {(() => {
                            // Get all column keys
                            const keys = Object.keys(data.node_input_df_3[0]);
                            
                            // Check if "year" column exists (case insensitive)
                            const yearColumnIndex = keys.findIndex(key => 
                              key.toLowerCase() === 'year'
                            );
                            
                            // If year column exists, reorder keys to put it first
                            let orderedKeys = [...keys];
                            if (yearColumnIndex !== -1) {
                              const yearColumn = orderedKeys.splice(yearColumnIndex, 1)[0];
                              orderedKeys = [yearColumn, ...orderedKeys];
                            }
                            
                            return orderedKeys.map((key) => (
                              <th key={key} className="column-header">
                                <div className="column-header-content">
                                  <span>{key}</span>
                                  <button 
                                    className="copy-button"
                                    onClick={() => copyToClipboard(key)}
                                    title="Copy column name"
                                  >
                                    <MdOutlineContentCopy 
                                      size={16} 
                                      color={copiedColumn === key ? "#4CAF50" : "#666"} 
                                    />
                                  </button>
                                </div>
                              </th>
                            ));
                          })()}
                        </tr>
                      </thead>
                      <tbody>
                        {data.node_input_df_3.map((row, index) => {
                          // Get all column keys
                          const keys = Object.keys(row);
                          
                          // Check if "year" column exists (case insensitive)
                          const yearColumnIndex = keys.findIndex(key => 
                            key.toLowerCase() === 'year'
                          );
                          
                          // If year column exists, reorder keys to put it first
                          let orderedKeys = [...keys];
                          if (yearColumnIndex !== -1) {
                            const yearColumn = orderedKeys.splice(yearColumnIndex, 1)[0];
                            orderedKeys = [yearColumn, ...orderedKeys];
                          }
                          
                          return (
                            <tr key={index}>
                              {orderedKeys.map((key, idx) => (
                                <td key={idx}>
                                  {((nodeType === 'extrapolation' || nodeType === 'finance') || (nodeType === 'operationalcosts' && activeInputTab === 1)) && typeof row[key] === 'number' ? formatIndianNumber(row[key]) : row[key]}
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="row-info">
                      Showing {data.node_input_df_3.length} rows of {data.node_input_df_length3 || data.node_input_df_lenght3 || "unknown"}
                    </div>
                  </div>
                )}
              </div>
              <div className="divider"></div>
            </>
          )}
          <div className="table-container">
            <h4 className="table-title" style={{ borderBottomColor: nodeColor }}>
              Node Output Data 
              <span style={{ fontWeight: 'normal', fontSize: '0.9em', color: '#666', marginLeft: '4px' }}>({nodeName})</span>
            </h4>
            {outputTables.length > 1 && (
              <div className="output-tabs">
                {outputTables.map((output, index) => (
                  <button
                    key={`${output.label}-${index}`}
                    type="button"
                    className={`output-tab ${activeOutputTab === index ? 'active' : ''}`}
                    style={{ '--tab-color': nodeColor }}
                    onClick={() => setActiveOutputTab(index)}
                  >
                    {output.label}
                  </button>
                ))}
              </div>
            )}
             
            {/* Show chart visualization if enabled for matcher or adder nodes */}
            {(nodeType === 'matcher' || nodeType === 'adder') && showVisualization ? (
              <DataVisualization 
                data={data}
                selectedColumns={selectedColumns}
                handleColumnSelect={handleColumnSelect}
                nodeName={nodeName}
                pipelineName={pipelineName}
                onSwitchToTable={() => setShowVisualization(false)}
              />
            ) : (
              <div className="scrollable-table">
                <table>
                  <thead>
                    <tr>
                      {(() => {
                        // Get all column keys
                        const keys = Object.keys(activeOutputRows[0] || {});
                        
                        // Check if "year" column exists (case insensitive)
                        const yearColumnIndex = keys.findIndex(key => 
                          key.toLowerCase() === 'year'
                        );
                        
                        // If year column exists, reorder keys to put it first
                        let orderedKeys = [...keys];
                        if (yearColumnIndex !== -1) {
                          const yearColumn = orderedKeys.splice(yearColumnIndex, 1)[0];
                          orderedKeys = [yearColumn, ...orderedKeys];
                        }
                        
                        // Map the ordered keys to table headers
                        return orderedKeys.map((key) => (
                          <th key={key} className="column-header">
                            <div className="column-header-content">
                              {/* Add checkbox for matcher and adder node columns (except date/time/month columns) */}
                              {(nodeType === 'matcher' || nodeType === 'adder') && 
                                !key.toLowerCase().includes('date') && 
                                !key.toLowerCase().includes('time') && 
                                !key.toLowerCase().includes('month') && (
                                <input
                                  type="checkbox"
                                  className="column-checkbox"
                                  checked={selectedColumns.includes(key)}
                                  onChange={() => handleColumnSelect(key)}
                                  title="Select for visualization"
                                />
                              )}
                              <span>
                                {key}
                                {/* Add x-axis indicator for date/time/month columns */}
                                {(nodeType === 'matcher' || nodeType === 'adder') && 
                                  (key.toLowerCase().includes('date') || 
                                  key.toLowerCase().includes('time') || 
                                  key.toLowerCase().includes('month')) && (
                                  <span className="x-axis-indicator" title="Used as X-axis for visualization">
                                    (X-axis)
                                  </span>
                                )}
                              </span>
                              <button 
                                className="copy-button"
                                onClick={() => copyToClipboard(key)}
                                title="Copy column name"
                              >
                                <MdOutlineContentCopy 
                                  size={16} 
                                  color={copiedColumn === key ? "#4CAF50" : "#666"} 
                                />
                              </button>
                            </div>
                            {/* Display sum value if available and not a date/time/month column */}
                            {data.sum && 
                             activeOutput.isPrimary &&
                             data.sum[key] !== undefined && 
                             nodeType !== 'finance' && 
                             nodeType !== 'extrapolation' &&
                             nodeType !== 'loan' &&
                             nodeType !== 'depreciation' &&
                             nodeType !== 'operationalcosts' &&
                             !key.toLowerCase().includes('date') && 
                             !key.toLowerCase().includes('time') && 
                             !key.toLowerCase().includes('month') && (
                              <div 
                                className="sum-value" 
                                title={`Sum of all values in this column: ${formatIndianNumber(data.sum[key])}`}
                              >
                                <span>Σ: {formatIndianNumber(data.sum[key])}</span>
                              </div>
                            )}
                          </th>
                        ));
                      })()}
                    </tr>
                  </thead>
                  <tbody>
                    {activeOutputRows.map((row, index) => {
                      // Get all column keys
                      const keys = Object.keys(row);
                      
                      // Check if "year" column exists (case insensitive)
                      const yearColumnIndex = keys.findIndex(key => 
                        key.toLowerCase() === 'year'
                      );
                      
                      // If year column exists, reorder keys to put it first
                      let orderedKeys = [...keys];
                      if (yearColumnIndex !== -1) {
                        const yearColumn = orderedKeys.splice(yearColumnIndex, 1)[0];
                        orderedKeys = [yearColumn, ...orderedKeys];
                      }
                      
                      // Check if this is a finance node and if we should highlight this row
                      const isFinanceNode = nodeType === 'finance';
                      const isOperationalCostsNode = nodeType === 'operationalcosts';
                      const totalRows = activeOutputRows.length;
                      const isInLastSevenRows = isFinanceNode && (index >= totalRows - 7);
                      const isInLastTwoRows = isOperationalCostsNode && (index >= totalRows - 2);
                      
                      return (
                        <tr key={index} style={isInLastTwoRows ? { backgroundColor: '#fef3c7' } : {}}>
                          {orderedKeys.map((key, idx) => {
                            // Highlight first 2 columns in last 7 rows for finance nodes
                            const shouldHighlight = isInLastSevenRows && idx < 2;
                            return (
                              <td 
                                key={idx} 
                                style={shouldHighlight ? { backgroundColor: '#fef3c7', fontWeight: 'bold' } : {}}
                              >
                                {(nodeType === 'extrapolation' || nodeType === 'banking' || nodeType === 'finance' || nodeType === 'operationalcosts' || nodeType === 'battery') && typeof row[key] === 'number' ? formatIndianNumber(row[key]) : row[key]}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="row-info">
                  <div>
                    Showing {activeOutputRows.length} rows of {activeOutputLength}
                    {/* Debug info */}
                    <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666' }}>
                      (Node type: {nodeType || 'unknown'}{data?.node_type ? `, API: ${data.node_type}` : ''})
                    </span>
                  </div>
                  <div className="download-buttons-container">
                    <button 
                      className="download-button"
                      onClick={() => download_node_output(pipelineName, nodeName, session.last_run_id)}
                      title="Download full output data as CSV"
                    >
                      <FiDownload size={16} /> Download CSV
                    </button>
                    
                    {/* Always show the analysis download button for now */}
                    {/* Debug info: nodeType={nodeType}, data.node_type={data?.node_type}, bankingType={data?.node_type_details?.bankingType} */}
                    {(nodeType === 'banking') && (
                      <button
                        className="download-button policy-download-button"
                        onClick={() => downloadAnalysisWorkbook(pipelineName, session.last_run_id)}
                        title="Download analysis files as Excel workbook"
                      >
                        <FiDownload size={16} /> Download Analysis Workbook
                      </button>
                    )}
                  </div>
                </div>
                {/* Show visualization hint for matcher and adder nodes */}
                {(nodeType === 'matcher' || nodeType === 'adder') && selectedColumns.length > 0 && (
                  <div className="visualization-hint">
                    <p>{selectedColumns.length} column(s) selected for visualization.</p>
                    <button 
                      onClick={toggleVisualization}
                      className="show-visualization-btn"
                    >
                      Show Visualization
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataInspector;
